import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BookMarked, Lock, AlertTriangle, Wrench, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";

const STATUS_CLS = {
  "Verified & locked": "bg-teal-50 text-teal-700 border-teal-200",
  "Partially verified": "bg-sky-50 text-sky-700 border-sky-200",
  "Citation conflict": "bg-red-50 text-red-700 border-red-200",
};
const CHECK_CLS = {
  Verified: "bg-teal-50 text-teal-700 border-teal-200",
  Pending: "bg-sky-50 text-sky-700 border-sky-200",
  Conflict: "bg-red-50 text-red-700 border-red-200",
  "Not applicable": "bg-slate-100 text-slate-400 border-slate-200",
};
const FIELD_LABELS = [["title", "Title"], ["authors", "Authors"], ["journal_or_issuing_body", "Journal / issuing body"],
  ["year", "Year"], ["volume", "Volume"], ["issue", "Issue"], ["pages", "Pages"], ["doi", "DOI"],
  ["pmid", "PMID"], ["registry_number", "Registry number"], ["url", "URL"], ["access_date", "Access date"]];

function CitationRow({ c }) {
  const [open, setOpen] = useState(c.status === "Citation conflict");
  return (
    <div className={`bg-white border rounded-xl shadow-sm ${c.status === "Citation conflict" ? "border-red-300" : "border-slate-200/80"}`} data-testid={`citation-${c.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-4 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-slate-400">{c.id} · {c.uid}</span>
        <span className="text-xs font-bold text-slate-800 flex-1 min-w-[240px] leading-snug">{c.fields.title}</span>
        {c.locked_fields.length > 0 && <span className="flex items-center gap-1 text-[9px] text-slate-500"><Lock className="w-3 h-3 text-teal-600" />{c.locked_fields.length} fields locked</span>}
        <Badge variant="outline" className={`text-[9px] ${STATUS_CLS[c.status]}`} data-testid={`citation-status-${c.evidence_id}`}>{c.status}</Badge>
        {!c.approved_for_outputs && <Badge variant="outline" className="text-[9px] bg-red-600 text-white border-red-600">Removed from approved outputs</Badge>}
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
            {FIELD_LABELS.map(([f, label]) => (
              <div key={f}>
                <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                  {label}{c.locked_fields.includes(f) && <Lock className="w-2.5 h-2.5 text-teal-600" />}
                </p>
                <p className={`text-[11px] break-all ${c.conflicting_fields.some((x) => x.field === f) ? "text-red-700 font-semibold" : "text-slate-700"}`}>
                  {Array.isArray(c.fields[f]) ? c.fields[f].join(", ") : (c.fields[f] ?? "—")}
                </p>
              </div>
            ))}
          </div>
          {c.conflicting_fields.length > 0 && (
            <div className="border border-red-200 bg-red-50/60 rounded-lg p-3" data-testid={`citation-conflicts-${c.evidence_id}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-red-700 mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Metadata conflicts — displayed vs verified record</p>
              <div className="space-y-1">
                {c.conflicting_fields.map((x) => (
                  <p key={x.field} className="text-[11px] text-slate-700"><span className="font-semibold">{x.label}:</span>{" "}
                    <span className="line-through text-red-600">{x.displayed}</span> → <span className="font-semibold text-teal-800">{x.verified}</span></p>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {c.checks.map((k) => (
              <div key={k.id} className="border border-slate-200 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10.5px] font-semibold text-slate-700">{k.name}</p>
                  <Badge variant="outline" className={`text-[9px] shrink-0 ${CHECK_CLS[k.status]}`}>{k.status}</Badge>
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{k.detail}</p>
              </div>
            ))}
          </div>
          {c.notes && <p className="text-[10px] text-slate-400 italic">{c.notes}</p>}
        </div>
      )}
    </div>
  );
}

export function CitationValidation() {
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => { api.get("/citations").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-6 text-slate-400 text-sm" data-testid="citations-loading">Loading citation register…</div>;

  const applyRepair = async (taskId) => {
    setApplying(true);
    try {
      const r = await api.post(`/citations/repair/${taskId}`);
      setData(r.data);
      toast.success("Citation repaired — verified fields locked and record restored to approved outputs.");
    } catch {
      toast.error("Repair could not be applied.");
    } finally {
      setApplying(false);
    }
  };

  const s = data.summary;
  return (
    <div className="space-y-4" data-testid="citation-validation">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="citation-tiles">
        {[["References under validation", s.total, "text-slate-800"],
          ["Verified & locked", s.verified_locked, "text-teal-700"],
          ["Partially verified", s.partially_verified, "text-sky-700"],
          ["Citation conflicts", s.conflicts, s.conflicts ? "text-red-600" : "text-slate-400"],
          ["Removed from approved outputs", s.excluded_from_outputs, s.excluded_from_outputs ? "text-red-600" : "text-slate-400"]].map(([label, n, cls]) => (
          <div key={label} className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm">
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-tight">{label}</p>
            <p className={`font-heading text-xl font-bold ${cls}`}>{n}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5 text-teal-600" />Validation policy</p>
        <p className="text-[11.5px] text-slate-600 leading-relaxed">{data.policy}</p>
        <p className="text-[10px] text-slate-400 mt-2">Verification sources: {data.verification_sources.join(" · ")} · Access date {data.access_date}</p>
      </div>

      {data.repair_tasks.map((t) => (
        <div key={t.id} className={`border rounded-xl p-4 shadow-sm ${t.status === "Open" ? "bg-amber-50/60 border-amber-300" : "bg-white border-slate-200/80"}`} data-testid={`repair-task-${t.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 flex-1 min-w-[260px]">
              {t.status === "Open" ? <Wrench className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />}
              <div>
                <p className="text-xs font-bold text-slate-900">{t.title} <span className="font-mono text-[9px] text-slate-400 ml-1">{t.id} · {t.priority} priority</span></p>
                <p className="text-[11px] text-slate-600 mt-1 leading-relaxed max-w-3xl">{t.description}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {t.field_corrections.map((f) => (
                    <span key={f.field} className="text-[10px] text-slate-600"><span className="font-mono uppercase text-slate-400">{f.field}</span>{" "}
                      <span className="line-through text-red-600">{f.current}</span> → <span className="font-semibold text-teal-800">{f.corrected}</span></span>
                  ))}
                </div>
                {t.status === "Completed" && <p className="text-[10px] text-teal-700 mt-1.5">Applied by {t.applied_by} · {t.applied_at?.slice(0, 10)} · verified fields locked and record restored to approved outputs.</p>}
              </div>
            </div>
            {t.status === "Open" ? (
              <button onClick={() => applyRepair(t.id)} disabled={applying} data-testid={`apply-repair-${t.id}`}
                className="text-[11px] font-semibold bg-slate-900 text-white rounded-full px-4 py-2 hover:bg-slate-700 transition-colors disabled:opacity-50">
                {applying ? "Applying…" : "Apply verified correction"}
              </button>
            ) : (
              <Badge variant="outline" className="text-[9px] bg-teal-50 text-teal-700 border-teal-200">Completed</Badge>
            )}
          </div>
        </div>
      ))}

      <div className="space-y-2.5" data-testid="citation-register">
        {data.citations.map((c) => <CitationRow key={c.id} c={c} />)}
      </div>
    </div>
  );
}
